import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MousePointer2, TrendingUp, TrendingDown, Minus, Activity, Building2, CreditCard, Sparkles, Users, MapPin } from 'lucide-react';
import { format, formatDistanceToNow, subDays } from 'date-fns';
import { api } from '../../lib/api';
import PageLoader from '../shared/PageLoader';
import AdminOrgMap from './AdminOrgMap';
import './AdminDashboardV2.css';

const PERIODS = [
  { key: '1d',  label: '24h', days: 1 },
  { key: '7d',  label: '7d',  days: 7 },
  { key: '30d', label: '30d', days: 30 },
  { key: '90d', label: '90d', days: 90 },
  { key: 'all', label: 'All', days: 9999 },
];

// Wide aspect ratio so the SVG fills the canvas horizontally without
// growing tall on wide screens. preserveAspectRatio="xMidYMid meet"
// scales to whichever dimension is the binding constraint.
const VIEW_W = 1200, VIEW_H = 320;

function placeDots(orgs) {
  const N = orgs.length;
  if (!N) return [];
  const ringCount = Math.min(7, Math.max(2, Math.ceil(Math.sqrt(N) / 1.2)));
  // ELLIPTICAL rings: wide horizontally to fill the panoramic viewBox, but
  // vertically bounded so every ring's apex stays inside VIEW_H. (The old
  // circular rings used R=540 on a 320-tall canvas — dots at steep angles
  // landed above y=0 and were clipped, so only ~half the orgs rendered.)
  const A_OUTER = 560, A_INNER = 120;  // horizontal semi-axis
  const B_OUTER = 245, B_INNER = 60;   // vertical semi-axis — apex y ≥ ~45
  const t = (i) => i / Math.max(ringCount - 1, 1);
  const ringA = (i) => A_OUTER - (A_OUTER - A_INNER) * t(i);
  const ringB = (i) => B_OUTER - (B_OUTER - B_INNER) * t(i);
  const cx = VIEW_W / 2, cy = VIEW_H - 30;

  const weights = Array.from({ length: ringCount }, (_, i) => ringA(i));
  const totalW = weights.reduce((a, b) => a + b, 0);
  const counts = weights.map(w => Math.max(1, Math.round(N * w / totalW)));
  const placed = counts.reduce((a, b) => a + b, 0);
  counts[counts.length - 1] += (N - placed);

  const positions = [];
  let idx = 0;
  for (let i = 0; i < ringCount; i++) {
    const a = ringA(i), b = ringB(i);
    const count = counts[i];
    for (let j = 0; j < count; j++) {
      if (idx >= N) break;
      const theta = Math.PI * (j + 0.5) / count;
      positions.push({
        org: orgs[idx++],
        cx: cx - a * Math.cos(theta),
        cy: cy - b * Math.sin(theta),
      });
    }
  }
  return positions;
}

function typeColor(type) {
  if (type === 'medical_practice') return '#0DA7CA';
  if (type === 'law_firm')         return '#8b5cf6';
    if (type === 'marketer')         return '#a78bfa';
  return '#6b7280';
}
function isFilled(o) {
  return o.subscription?.status === 'active' || o.subscription?.status === 'trialing' || o.type === 'law_firm' || o.type === 'marketer';
}

// Sparkline that draws a path through `data` values within a 100x28 box.
function Sparkline({ data, color = '#0DA7CA' }) {
  if (!data?.length) return null;
  const W = 100, H = 28;
  const max = Math.max(1, ...data);
  const stepX = W / Math.max(1, data.length - 1);
  const points = data.map((v, i) => `${i * stepX},${H - (v / max) * H}`).join(' ');
  return (
    <svg className="ad-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// Map a KPI delta to a trend pill
function Trend({ delta }) {
  if (delta == null) return null;
  if (delta === 0) return <span className="ad-kpi-trend flat"><Minus size={10} /> No change</span>;
  const up = delta > 0;
  return (
    <span className={`ad-kpi-trend ${up ? 'up' : 'down'}`}>
      {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />} {up ? '+' : ''}{delta} vs prev
    </span>
  );
}

export default function AdminDashboardV2() {
  const [orgs, setOrgs] = useState([]);
  const [edges, setEdges] = useState([]);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [period, setPeriod] = useState('30d');
  const [typeFilter, setTypeFilter] = useState(new Set(['medical_practice', 'law_firm']));
  const [subFilter, setSubFilter] = useState(new Set(['active', 'none']));    // 'active' | 'none' | 'past_due'
  const [activityFilter, setActivityFilter] = useState('any');                // 'any' | 'recent'

  const [hovered, setHovered] = useState(null);
  const [locations, setLocations] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [pinned, setPinned] = useState(null);
  const [scrubIdx, setScrubIdx] = useState(null); // index into series.dates; null = "now"

  useEffect(() => {
    Promise.all([
      api.getOrgsOverview(),
      api.getAdminTimeseries(90),
    ])
      .then(async ([ovRes, seriesRes]) => {
        if (Array.isArray(ovRes)) {
          // Old backend shape (deploy mid-flight) — edges were a second call.
          setOrgs((ovRes || []).filter((o) => o.type !== 'marketer')); // marketers have their own tab
          setEdges(await api.getPartnershipEdges().catch(() => []));
        } else {
          setOrgs((ovRes?.orgs || []).filter((o) => o.type !== 'marketer'));
          setEdges(ovRes?.edges || []);
          setLocations(ovRes?.locations || []);
        }
        setSeries(seriesRes);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
    api.getAdminReferralNetwork()
      .then(d => setReferrals(d?.pairs || []))
      .catch(() => {});
  }, []);

  // "All" should let the scrubber walk back further than the default window.
  useEffect(() => {
    if (period !== 'all' || !series || series.days >= 365) return;
    api.getAdminTimeseries(365).then(setSeries).catch(() => {});
  }, [period]); // eslint-disable-line

  // The reference date for "active" / scrubber. Default = now.
  const asOf = useMemo(() => {
    if (scrubIdx == null || !series) return null;
    return new Date(series.dates[scrubIdx] + 'T23:59:59Z');
  }, [scrubIdx, series]);

  // Filter orgs by user filters + asOf
  const visibleOrgs = useMemo(() => {
    return orgs.filter(o => {
      if (asOf && new Date(o.created_at) > asOf) return false;
      if (!typeFilter.has(o.type)) return false;
      const sub = o.subscription?.status;
      const hasActive = sub === 'active' || sub === 'trialing';
      const isPastDue = sub === 'past_due' || sub === 'incomplete'; // problem states share a bucket
      const noSub     = !sub;
      if (hasActive && !subFilter.has('active')) return false;
      if (isPastDue && !subFilter.has('past_due')) return false;
      if (noSub && o.type !== 'law_firm' && o.type !== 'marketer' && !subFilter.has('none')) return false;
      if (activityFilter === 'recent' && !o.recently_active) return false;
      return true;
    });
  }, [orgs, typeFilter, subFilter, activityFilter, asOf]);

  // KPIs computed from current filter set + period
  const kpis = useMemo(() => {
    const periodDays = PERIODS.find(p => p.key === period)?.days || 30;
    const cutoff = subDays(new Date(), periodDays);
    const previousCutoff = subDays(new Date(), periodDays * 2);

    const total = visibleOrgs.length;
    const activeSubs = visibleOrgs.filter(o => o.subscription?.status === 'active' || o.subscription?.status === 'trialing').length;

    // Real MRR from Stripe-synced quantity × unit_amount (migration 027).
    // Legacy subs created before 027 have no unit_amount — those were sold
    // at $500/mo. Trialing subs are $0 until they convert, so they're shown
    // as pipeline, not revenue.
    // What we actually bill: list price less any Stripe coupon (migration
    // 043). A 100%-off comped account contributes $0, not list price.
    const subMonthly = (s) => {
      const list = (s.quantity || 1) * (s.unit_amount ?? 50000);
      let net = list;
      if (s.discount_percent) net = net * (1 - Number(s.discount_percent) / 100);
      if (s.discount_amount_off) net = net - Number(s.discount_amount_off);
      return Math.max(0, net) / 100;
    };
    const payingSubs = visibleOrgs.filter(o => o.subscription?.status === 'active');
    const mrr = payingSubs.reduce((sum, o) => sum + subMonthly(o.subscription), 0);
    const trialMrr = visibleOrgs
      .filter(o => o.subscription?.status === 'trialing')
      .reduce((sum, o) => sum + subMonthly(o.subscription), 0);
    const newInPeriod = orgs.filter(o => new Date(o.created_at) > cutoff && typeFilter.has(o.type)).length;
    const newPrevious = orgs.filter(o => {
      const c = new Date(o.created_at);
      return c > previousCutoff && c <= cutoff && typeFilter.has(o.type);
    }).length;
    const active24h = orgs.filter(o => o.recently_active && typeFilter.has(o.type)).length;

    // Per-day spark over the visible window
    const sparkLen = Math.min(periodDays, 60);
    const orgSpark = series?.orgs?.slice(-sparkLen) || [];
    const subSpark = series?.new_subs?.slice(-sparkLen) || [];
    // Prefer real login activity once the logins series has data (migration
    // 036); appointments remain the proxy until then.
    const activitySeries = series?.logins?.some(v => v) ? series.logins : series?.appointments;
    const apptSpark = activitySeries?.slice(-sparkLen) || [];
    const newSpark = orgSpark;

    // Total signed-up locations: every org is at least one physical place
    // (its signup address); orgs that registered explicit locations count
    // each of them (Tesla MRI = 8, City Surgery = 9).
    const locCounts = new Map();
    for (const l of locations) locCounts.set(l.org_id, (locCounts.get(l.org_id) || 0) + 1);
    const locationCount = visibleOrgs.reduce((n, o) => n + (locCounts.get(o.id) || 1), 0);

    return {
      total, activeSubs, mrr, trialMrr, payingCount: payingSubs.length, newInPeriod, active24h,
      locationCount,
      newDelta: newInPeriod - newPrevious,
      orgSpark, subSpark, apptSpark, newSpark,
    };
  }, [visibleOrgs, orgs, typeFilter, period, series, locations]);

  const positions = useMemo(() => placeDots(visibleOrgs), [visibleOrgs]);
  const selected = orgs.find(o => o.id === (pinned || hovered)) || null;

  // Map id → {cx, cy} so we can look up positions for edge rendering.
  const positionById = useMemo(() => {
    const m = new Map();
    for (const p of positions) m.set(p.org.id, { cx: p.cx, cy: p.cy });
    return m;
  }, [positions]);

  // Edges that are renderable: both endpoints are currently visible.
  const visibleEdges = useMemo(() => {
    return edges.filter(e => positionById.has(e.a) && positionById.has(e.b));
  }, [edges, positionById]);

  // The currently-focused org id (pinned beats hovered for stickiness)
  const focusId = pinned || hovered;

  // For each org, count its visible connections — used to highlight
  // edges connected to the focused dot.
  const isEdgeFocused = (e) => focusId && (e.a === focusId || e.b === focusId);

  // Max of the daily-orgs series — hoisted so the scrubber's bar loop
  // doesn't recompute it once per bar.
  const scrubMax = useMemo(() => Math.max(1, ...(series?.orgs || [1])), [series]);

  const toggle = (set, setter) => (val) => {
    const next = new Set(set);
    if (next.has(val)) next.delete(val); else next.add(val);
    if (next.size === 0) next.add(val); // never empty
    setter(next);
  };

  if (loading) return <div className="ad"><PageLoader variant="block" sub="Admin" label="Loading platform overview…" /></div>;
  if (error)   return <div className="ad"><div style={{ color: '#dc2626' }}>{error}</div></div>;

  return (
    <div className="ad">
      <div className="ad-header">
        <div>
          <div className="ad-eyebrow">Platform overview</div>
          <h1 className="ad-title">Admin dashboard</h1>
        </div>
        <div className="ad-period">
          {PERIODS.map(p => (
            <button key={p.key} className={period === p.key ? 'is-active' : ''} onClick={() => setPeriod(p.key)}>{p.label}</button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="ad-kpis">
        <div className="ad-kpi">
          <div className="ad-kpi-label"><MapPin size={10} style={{ display: 'inline', marginRight: 4 }} />Total locations</div>
          <div className="ad-kpi-value">{kpis.locationCount}</div>
          <Link
            to="/admin/organizations"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 11, color: 'var(--muted, #6b7280)', textDecoration: 'none',
            }}
          >
            <Building2 size={10} />
            {kpis.total} organization{kpis.total === 1 ? '' : 's'}
          </Link>
          <Sparkline data={kpis.orgSpark} color="#0DA7CA" />
        </div>
        <div className="ad-kpi">
          <div className="ad-kpi-label"><CreditCard size={10} style={{ display: 'inline', marginRight: 4 }} />Active subs</div>
          <div className="ad-kpi-value">{kpis.activeSubs}</div>
          <Sparkline data={kpis.subSpark} color="#059669" />
        </div>
        <div className="ad-kpi">
          <div className="ad-kpi-label">MRR</div>
          <div className="ad-kpi-value">${kpis.mrr.toLocaleString()}</div>
          <div className="ad-kpi-trend flat">
            {kpis.trialMrr > 0
              ? <>+${kpis.trialMrr.toLocaleString()}/mo in trials</>
              : <>{kpis.payingCount} paying</>}
          </div>
        </div>
        <div className="ad-kpi">
          <div className="ad-kpi-label"><Sparkles size={10} style={{ display: 'inline', marginRight: 4 }} />New ({period})</div>
          <div className="ad-kpi-value">{kpis.newInPeriod}</div>
          <Trend delta={kpis.newDelta} />
          <Sparkline data={kpis.newSpark} color="#8b5cf6" />
        </div>
        <div className="ad-kpi">
          <div className="ad-kpi-label"><Activity size={10} style={{ display: 'inline', marginRight: 4 }} />Active 24h</div>
          <div className="ad-kpi-value">{kpis.active24h}</div>
          <Sparkline data={kpis.apptSpark} color="#0DA7CA" />
        </div>
      </div>

      {/* Filters */}
      <div className="ad-filters">
        <div className="ad-filter-group">
          <span className="ad-filter-label">Type</span>
          <button className={`ad-chip ${typeFilter.has('medical_practice') ? 'is-on' : ''}`} onClick={() => toggle(typeFilter, setTypeFilter)('medical_practice')}>Medical</button>
          <button className={`ad-chip ${typeFilter.has('law_firm') ? 'is-on' : ''}`} onClick={() => toggle(typeFilter, setTypeFilter)('law_firm')}>Law firm</button>
        </div>
        <div className="ad-filter-group">
          <span className="ad-filter-label">Sub</span>
          <button className={`ad-chip ${subFilter.has('active') ? 'is-on' : ''}`} onClick={() => toggle(subFilter, setSubFilter)('active')}>Active</button>
          <button className={`ad-chip ${subFilter.has('past_due') ? 'is-on' : ''}`} onClick={() => toggle(subFilter, setSubFilter)('past_due')}>Past due</button>
          <button className={`ad-chip ${subFilter.has('none') ? 'is-on' : ''}`} onClick={() => toggle(subFilter, setSubFilter)('none')}>No sub</button>
        </div>
        <div className="ad-filter-group">
          <span className="ad-filter-label">Activity</span>
          <button className={`ad-chip ${activityFilter === 'any' ? 'is-on' : ''}`} onClick={() => setActivityFilter('any')}>Any</button>
          <button className={`ad-chip ${activityFilter === 'recent' ? 'is-on' : ''}`} onClick={() => setActivityFilter('recent')}>Active 24h</button>
        </div>
        <div className="ad-filter-spacer" />
        <div className="ad-filter-count">{visibleOrgs.length} of {orgs.length} orgs</div>
      </div>

      {/* Constellation + inspector */}
      <div className="ad-canvas">
        <div className="ad-svg-wrap">
          <svg className="ad-svg" viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="xMidYMid meet">
            <line x1={VIEW_W / 2 - 540} y1={VIEW_H - 30} x2={VIEW_W / 2 + 540} y2={VIEW_H - 30} stroke="rgba(11,11,20,0.06)" />

            {/* Partnership edges — drawn UNDER the dots so dots stay readable.
                On focus (hover/pin) the focused org's edges brighten and
                everything else dims, so the network of one org is legible. */}
            {visibleEdges.map((e, i) => {
              const pa = positionById.get(e.a);
              const pb = positionById.get(e.b);
              const focused = isEdgeFocused(e);
              const dimmed  = focusId && !focused;
              return (
                <line key={`${e.a}-${e.b}-${i}`}
                  x1={pa.cx} y1={pa.cy} x2={pb.cx} y2={pb.cy}
                  stroke="#0DA7CA"
                  strokeWidth={focused ? 1.4 : 0.6}
                  strokeOpacity={focused ? 0.7 : (dimmed ? 0.04 : 0.15)}
                  style={{ transition: 'stroke-opacity 0.2s ease, stroke-width 0.2s ease', pointerEvents: 'none' }}
                />
              );
            })}

            {positions.map(({ org, cx, cy }, i) => {
              const filled = isFilled(org);
              const color  = typeColor(org.type);
              const isHov  = hovered === org.id || pinned === org.id;
              const dur    = (10 + (i % 7)) + 's';
              const delay  = -((i * 0.41) % 12) + 's';
              return (
                <g key={org.id} className="ad-drift" style={{ '--dur': dur, '--delay': delay }}>
                  {/* Visible dot — pointer-events disabled so growing on hover
                      doesn't trigger reflows on the hit-area below. */}
                  <circle
                    className={`ad-dot ${isHov ? 'is-hov' : ''} ${org.recently_active ? 'is-pulse' : ''}`}
                    cx={cx} cy={cy} r={isHov ? 5.5 : 3.5}
                    fill={filled ? color : 'transparent'}
                    stroke={color} strokeWidth={filled ? 0 : 1.2}
                    style={{ color, pointerEvents: 'none' }}
                  />
                  {/* Stable invisible hit area — fixed radius, never grows.
                      Owns hover/click so the visible dot can resize freely. */}
                  <circle
                    cx={cx} cy={cy} r={12}
                    fill="transparent" style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHovered(org.id)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => setPinned(p => p === org.id ? null : org.id)}
                  />
                </g>
              );
            })}
          </svg>
        </div>

        <div className="ad-inspector">
          <div className="ad-insp-eyebrow">Node inspector</div>
          {!selected ? (
            <div className="ad-insp-empty">
              <MousePointer2 size={28} />
              <div>Hover any dot to inspect.<br />Tap on mobile.</div>
            </div>
          ) : (
            <>
              <span className={`ad-insp-pill ${selected.type === 'medical_practice' ? 'med' : 'law'}`}>
                {selected.type === 'medical_practice' ? 'Medical' : 'Law firm'}
              </span>
              <h2 className="ad-insp-name">{selected.name}</h2>
              <p className="ad-insp-loc">{[selected.city, selected.state].filter(Boolean).join(', ') || '—'}</p>

              <div className="ad-insp-row"><span className="label">Members</span><span className="value">{selected.member_count}</span></div>
              <div className="ad-insp-row"><span className="label">Partnerships</span><span className="value">{selected.partnership_count}</span></div>
              <div className="ad-insp-row"><span className="label">Subscription</span>
                <span className="value">
                  {selected.subscription
                    ? <span className={`ad-insp-pill ${selected.subscription.status === 'active' || selected.subscription.status === 'trialing' ? 'success' : 'warning'}`}>{selected.subscription.status}</span>
                    : (selected.type === 'law_firm' || selected.type === 'marketer') ? <span className="ad-insp-pill muted">Free</span> : <span className="ad-insp-pill warning">None</span>
                  }
                </span>
              </div>
              <div className="ad-insp-row"><span className="label">Created</span><span className="value">{formatDistanceToNow(new Date(selected.created_at), { addSuffix: true })}</span></div>
              <div className="ad-insp-row"><span className="label">Activity 24h</span><span className="value">{selected.recently_active ? '✓ Live' : '—'}</span></div>

              <Link to={`/admin/organizations/${selected.id}`} className="ad-insp-link">
                Open organization <ArrowRight size={11} />
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Geographic map — same filter set as the constellation */}
      <div className="ad-map">
        <div className="ad-map-head">
          <span className="ad-map-title">Map — every organization</span>
          <span className="ad-map-note">
            {visibleOrgs.filter(o => o.lat != null && o.lng != null).length} of {visibleOrgs.length} pinned
            {visibleOrgs.some(o => o.lat == null || o.lng == null) && ' · un-geocoded orgs have no pin'}
            {' · '}
            <Link to="/admin/map" className="ad-map-open">Open full map <ArrowRight size={10} style={{ display: 'inline' }} /></Link>
          </span>
        </div>
        <div className="ad-map-canvas">
          <AdminOrgMap orgs={visibleOrgs} locations={locations} typeColor={typeColor} />
        </div>
      </div>

      {/* Referral flow between law firms and providers */}
      <div className="ad-map" style={{ marginTop: 18 }}>
        <div className="ad-map-head">
          <span className="ad-map-title">Referral flow — law firms ⇄ providers</span>
          <span className="ad-map-note">
            {referrals.length} active relationship{referrals.length === 1 ? '' : 's'} · shared patients, collaborative appointments, case referrals
          </span>
        </div>
        {referrals.length === 0 ? (
          <div style={{ padding: '28px 18px', textAlign: 'center', fontSize: 12.5, color: 'var(--muted, #6b7280)' }}>
            No firm↔provider referral activity yet. Relationships appear here once a patient is shared,
            a firm is added to an appointment, or a case records a referring organization.
          </div>
        ) : (
          <div style={{ padding: '6px 6px 10px' }}>
            {referrals.slice(0, 10).map((r, i) => (
              <div
                key={`${r.law_firm.id}-${r.provider.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  borderTop: i > 0 ? '1px solid var(--line, rgba(127,127,127,0.12))' : 'none',
                  flexWrap: 'wrap',
                }}
              >
                <Link to={`/admin/organizations/${r.law_firm.id}`} style={{ fontSize: 13, fontWeight: 600, color: '#8b5cf6', textDecoration: 'none' }}>
                  {r.law_firm.name}
                </Link>
                <span style={{ fontSize: 12, opacity: 0.5 }}>⇄</span>
                <Link to={`/admin/organizations/${r.provider.id}`} style={{ fontSize: 13, fontWeight: 600, color: '#0DA7CA', textDecoration: 'none' }}>
                  {r.provider.name}
                </Link>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {r.shared_patients > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: 'rgba(13,167,202,0.10)', color: '#0DA7CA' }}>
                      {r.shared_patients} shared patient{r.shared_patients === 1 ? '' : 's'}
                    </span>
                  )}
                  {r.collab_appointments > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: 'rgba(16,185,129,0.10)', color: '#10B981' }}>
                      {r.collab_appointments} shared appt{r.collab_appointments === 1 ? '' : 's'}
                    </span>
                  )}
                  {r.case_referrals > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: 'rgba(245,158,11,0.10)', color: '#F59E0B' }}>
                      {r.case_referrals} case referral{r.case_referrals === 1 ? '' : 's'}
                    </span>
                  )}
                </span>
              </div>
            ))}
            {referrals.length > 10 && (
              <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--muted, #6b7280)' }}>
                +{referrals.length - 10} more relationships
              </div>
            )}
          </div>
        )}
      </div>

      {/* Time scrubber */}
      {series && series.dates.length > 0 && (
        <div className="ad-scrub">
          <div className="ad-scrub-head">
            <div>
              <div className="ad-scrub-title">Time scrubber — drag to replay</div>
              <div className="ad-scrub-current">
                {scrubIdx == null
                  ? <>Showing <em>now</em></>
                  : <>State as of <em>{format(new Date(series.dates[scrubIdx]), 'MMM d, yyyy')}</em></>
                }
              </div>
            </div>
            {scrubIdx != null && (
              <button className="ad-chip is-on" onClick={() => setScrubIdx(null)}>Reset to now</button>
            )}
          </div>
          <div className="ad-bars">
            {series.dates.map((d, i) => {
              const v = series.orgs[i];
              const h = Math.max(2, (v / scrubMax) * 60);
              const isCur = scrubIdx === i;
              const isFut = scrubIdx != null && i > scrubIdx;
              return (
                <div key={d} className={`ad-bar ${isCur ? 'is-current' : ''} ${isFut ? 'is-future' : ''}`}
                     style={{ height: h }}
                     title={`${d}: ${v} new orgs`}
                     onClick={() => setScrubIdx(i)} />
              );
            })}
          </div>
          <div className="ad-scrub-axis">
            <span>{format(new Date(series.dates[0]), 'MMM d')}</span>
            <span>{format(new Date(series.dates[Math.floor(series.dates.length / 2)]), 'MMM d')}</span>
            <span>Today</span>
          </div>
        </div>
      )}
    </div>
  );
}
